import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { AlertTriangle, Plus, Edit, Trash2, User, Network, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { useRisks } from '@/hooks/queries';
import { RiskEditDialog } from '@/components/risks/RiskEditDialog';
import { RiskCreateDialog } from '@/components/risks/RiskCreateDialog';
import { LinkManager } from '@/components/shared/LinkManager';
import { EntityLinkedPreview } from '@/components/shared/EntityLinkedPreview';
import { useLinkStore } from '@/stores/linkStore';

const getRiskLevel = (score: number) => {
  if (score >= 20) return { label: 'Very High', color: 'bg-danger text-danger-foreground' };
  if (score >= 15) return { label: 'High', color: 'bg-orange-500 text-white' };
  if (score >= 10) return { label: 'Medium', color: 'bg-warning text-warning-foreground' };
  if (score >= 5) return { label: 'Low', color: 'bg-success text-success-foreground' };
  return { label: 'Very Low', color: 'bg-muted text-muted-foreground' };
};

const statusColors = {
  identified: 'bg-warning text-warning-foreground',
  assessing: 'bg-accent text-accent-foreground',
  treating: 'bg-success text-success-foreground',
  monitoring: 'bg-primary text-primary-foreground',
  closed: 'bg-muted text-muted-foreground',
};

const statusLabels = {
  identified: 'Identified',
  assessing: 'Assessing',
  treating: 'Treating',
  monitoring: 'Monitoring',
  closed: 'Closed',
};

export default function RisksPage() {
  const { organization, profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterAssigned = searchParams.get('filter') === 'assigned';
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [riskLevelFilter, setRiskLevelFilter] = useState<string>('all');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRiskId, setSelectedRiskId] = useState<string | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);

  const { loadLinks } = useLinkStore();

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const { 
    risks, 
    isLoading, 
    createRisk, 
    updateRisk, 
    deleteRisk, 
    isCreating,
    isUpdating,
    isDeleting 
  } = useRisks(organization?.id, { status: statusFilter, riskLevel: riskLevelFilter });

  const selectedRisk = risks?.find(r => r.id === selectedRiskId);

  const handleCreate = async (data: any, links?: Array<{id: string; type: any; name: string}>) => {
    if (!organization?.id) {
      console.error('No organization ID');
      return;
    }
    
    const dataToSubmit = {
      ...data,
      org_id: organization.id,
    };
    
    createRisk(dataToSubmit, {
      onSuccess: async (newRisk: any) => {
        if (newRisk?.id && links && links.length > 0) {
          const { useLinkStore } = await import('@/stores/linkStore');
          const { bulkCreateLinks } = useLinkStore.getState();
          
          for (const link of links) {
            await bulkCreateLinks('risk', newRisk.id, link.type, [link.id]);
          }
        }
        setAddDialogOpen(false);
      },
      onError: (error) => {
        console.error('Error in handleCreate callback:', error);
      }
    });
  };

  const handleEdit = (riskId: string) => {
    setSelectedRiskId(riskId);
    setEditDialogOpen(true);
  };

  const handleUpdate = (id: string, data: any) => {
    updateRisk({ id, data }, {
      onSuccess: () => {
        setEditDialogOpen(false);
      },
    });
  };

  const handleDeleteClick = (riskId: string) => {
    setSelectedRiskId(riskId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedRiskId) {
      deleteRisk(selectedRiskId, {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setSelectedRiskId(null);
          setDetailSheetOpen(false);
        },
      });
    }
  };

  const openDetailSheet = (riskId: string) => {
    setSelectedRiskId(riskId);
    setDetailSheetOpen(true);
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Risks</h1>
            <p className="text-muted-foreground">Monitor and manage organizational risks</p>
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-3 bg-muted rounded w-full mb-2"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const filteredRisks = risks?.filter(risk => {
    if (filterAssigned && risk.owner_id !== profile?.user_id) return false;
    return true;
  }) || [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Risks</h1>
          <p className="text-muted-foreground">Monitor and manage organizational risks</p>
        </div>
        <Button className="gap-2" onClick={() => setAddDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Add Risk
        </Button>
      </div>

      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="identified">Identified</SelectItem>
            <SelectItem value="assessed">Assessed</SelectItem>
            <SelectItem value="treated">Treated</SelectItem>
            <SelectItem value="monitored">Monitored</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>

        <Select value={riskLevelFilter} onValueChange={setRiskLevelFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by risk level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Risk Levels</SelectItem>
            <SelectItem value="very-high">Very High Risk</SelectItem>
            <SelectItem value="high">High Risk</SelectItem>
            <SelectItem value="medium">Medium Risk</SelectItem>
            <SelectItem value="low">Low Risk</SelectItem>
            <SelectItem value="very-low">Very Low Risk</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={filterAssigned ? "default" : "outline"}
          size="sm"
          onClick={() => {
            if (filterAssigned) {
              searchParams.delete('filter');
            } else {
              searchParams.set('filter', 'assigned');
            }
            setSearchParams(searchParams);
          }}
        >
          <User className="h-4 w-4 mr-2" />
          Assigned to me
        </Button>
      </div>

      {filteredRisks.length > 0 ? (
        <div className="space-y-4">
          {filteredRisks.map((risk) => {
            const riskLevel = getRiskLevel(risk.risk_score || 0);
            return (
              <Card 
                key={risk.id} 
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => openDetailSheet(risk.id)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-5 w-5 text-foreground" />
                        <CardTitle className="text-lg">{risk.title}</CardTitle>
                        <Badge className={riskLevel.color}>
                          {riskLevel.label} ({risk.risk_score || 0})
                        </Badge>
                        <Badge className={statusColors[risk.status as keyof typeof statusColors]}>
                          {statusLabels[risk.status as keyof typeof statusLabels]}
                        </Badge>
                      </div>
                      {risk.asset_or_process && (
                        <CardDescription>{risk.asset_or_process}</CardDescription>
                      )}
                      {/* Show linked entities */}
                      <div className="mt-2">
                        <EntityLinkedPreview entityType="risk" entityId={risk.id} maxDisplay={3} />
                      </div>
                    </div>
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDetailSheet(risk.id)}
                        title="View details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(risk.id)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteClick(risk.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    {(risk as any).owner && (
                      <div>
                        <p className="text-muted-foreground">Owner</p>
                        <p className="font-medium">{(risk as any).owner.name || (risk as any).owner.email}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-muted-foreground">Impact</p>
                      <p className="font-medium">{risk.impact || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Likelihood</p>
                      <p className="font-medium">{risk.likelihood || 'N/A'}</p>
                    </div>
                    {risk.next_review && (
                      <div>
                        <p className="text-muted-foreground">Next Review</p>
                        <p className="font-medium">{format(new Date(risk.next_review), 'MMM dd, yyyy')}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No risks found</h3>
            <p className="text-muted-foreground text-center mb-4">
              {filterAssigned 
                ? 'No risks are assigned to you.'
                : 'Start identifying and assessing risks to improve your security posture.'}
            </p>
            <Button className="gap-2" onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              Add Your First Risk
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Detail Sheet */}
      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Risk Details
              </SheetTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDetailSheetOpen(false);
                    handleEdit(selectedRiskId!);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteClick(selectedRiskId!)}
                  disabled={isDeleting}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </SheetHeader>

          {selectedRisk && (
            <div className="space-y-6 py-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">{selectedRisk.title}</h3>
                <div className="flex gap-2 mb-2">
                  <Badge className={getRiskLevel(selectedRisk.risk_score || 0).color}>
                    {getRiskLevel(selectedRisk.risk_score || 0).label} ({selectedRisk.risk_score || 0})
                  </Badge>
                  <Badge className={statusColors[selectedRisk.status as keyof typeof statusColors]}>
                    {statusLabels[selectedRisk.status as keyof typeof statusLabels]}
                  </Badge>
                </div>
                {selectedRisk.asset_or_process && (
                  <p className="text-sm text-muted-foreground">{selectedRisk.asset_or_process}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Impact</p>
                  <p className="font-medium">{selectedRisk.impact || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Likelihood</p>
                  <p className="font-medium">{selectedRisk.likelihood || 'N/A'}</p>
                </div>
                {selectedRisk.residual_score && (
                  <div>
                    <p className="text-muted-foreground">Residual Score</p>
                    <p className="font-medium">{selectedRisk.residual_score}</p>
                  </div>
                )}
                {selectedRisk.next_review && (
                  <div>
                    <p className="text-muted-foreground">Next Review</p>
                    <p className="font-medium">{format(new Date(selectedRisk.next_review), 'MMM dd, yyyy')}</p>
                  </div>
                )}
              </div>

              {selectedRisk.cause && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Cause</h4>
                  <p className="text-sm text-muted-foreground">{selectedRisk.cause}</p>
                </div>
              )}

              {selectedRisk.consequence && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Consequence</h4>
                  <p className="text-sm text-muted-foreground">{selectedRisk.consequence}</p>
                </div>
              )}

              {selectedRisk.treatment && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Treatment</h4>
                  <p className="text-sm text-muted-foreground">{selectedRisk.treatment}</p>
                </div>
              )}

              {selectedRisk.notes && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Notes</h4>
                  <p className="text-sm text-muted-foreground">{selectedRisk.notes}</p>
                </div>
              )}

              <Separator />

              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Network className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-medium">Related Entities</h4>
                </div>
                <LinkManager 
                  entityType="risk"
                  entityId={selectedRisk.id}
                  entityName={selectedRisk.title}
                  showHeader={false}
                  allowedTypes={['site', 'zone', 'system', 'endpoint', 'cloud_resource', 'control', 'evidence', 'supplier', 'incident', 'task']}
                />
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <RiskCreateDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSave={handleCreate}
        isCreating={isCreating}
      />

      {selectedRisk && (
        <RiskEditDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          risk={selectedRisk}
          onSave={handleUpdate}
          isSaving={isUpdating}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Risk</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedRisk?.title}"? This action cannot be undone.
              All related links will also be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-danger text-danger-foreground hover:bg-danger/90"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
